import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { motion, useReducedMotion } from 'framer-motion';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { EASE } from './constants';

interface NavItem {
    name: string;
    tag: string;
}

interface JournalNavProps {
    /** Section list — only needs { name, tag } from each entry */
    sections: NavItem[];
    /** Currently active tag (category slug), or null/undefined when on the journal index */
    activeTag?: string | null;
    /** When true (default), the nav pins below the main nav on scroll. */
    sticky?: boolean;
    /** Optional icon-only element placed flush-left with a hairline divider. */
    leftAction?: ReactNode;
    /** Optional icon-only element placed flush-right with a hairline divider. */
    rightAction?: ReactNode;
}

/**
 * Shared sticky sub-nav used across /journal and /journal/{slug} pages.
 * Links to category pages and includes a "Journal" home link.
 */
export default function JournalNav({ sections, activeTag, sticky = true, leftAction, rightAction }: JournalNavProps) {
    const [isPinned, setIsPinned] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const shouldReduceMotion = useReducedMotion();

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(([entry]) => setIsPinned(!entry.isIntersecting), { threshold: 0 });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    const isJournalHome = !activeTag;

    return (
        <>
            <div ref={sentinelRef} className="h-0" aria-hidden />
            <nav
                aria-label="Journal categories"
                className={cn(
                    'z-30 border-b transition-all duration-300',
                    sticky && 'sticky top-14 md:top-20',
                    sticky && isPinned ? 'border-[#F5F5F5]/10 bg-[#0e2727]/95 backdrop-blur-md' : 'border-transparent bg-[#0e2727]',
                )}
            >
                <div className="nav-px scrollbar-none mx-auto flex items-center gap-1 overflow-x-auto py-3 sm:gap-2">
                    {leftAction && (
                        <>
                            <span className="shrink-0 text-[#F5F5F5]/70 transition-colors hover:text-[#F5F5F5]">{leftAction}</span>
                            <span className="mx-2 h-3.5 w-px shrink-0 bg-[#F5F5F5]/20" aria-hidden />
                        </>
                    )}
                    <div className="mx-auto flex items-center gap-1 sm:gap-2">
                        {/* Journal home link */}
                        <Link
                            href="/journal"
                            className={cn(
                                'lux-caps lux-caps-sm relative shrink-0 px-3 py-1.5 transition-colors',
                                isJournalHome ? 'text-[#F5F5F5]' : 'text-[#F5F5F5]/50 hover:text-[#F5F5F5]/80',
                            )}
                        >
                            Journal
                            {isJournalHome && (
                                <motion.span
                                    layoutId="journal-nav-underline"
                                    className="absolute inset-x-1 -bottom-3 h-0.5 bg-[#F5F5F5]"
                                    transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3, ease: EASE }}
                                />
                            )}
                        </Link>

                        {/* Subtle separator */}
                        <span className="mx-1 h-3.5 w-px shrink-0 bg-[#F5F5F5]/20" aria-hidden />

                        {/* Category links */}
                        {sections.map((section) => (
                            <Link
                                key={section.tag}
                                href={`/journal/category/${section.tag}`}
                                className={cn(
                                    'lux-caps lux-caps-sm relative shrink-0 px-3 py-1.5 transition-colors',
                                    activeTag === section.tag ? 'text-[#F5F5F5]' : 'text-[#F5F5F5]/50 hover:text-[#F5F5F5]/80',
                                )}
                            >
                                {section.name}
                                {activeTag === section.tag && (
                                    <motion.span
                                        layoutId="journal-nav-underline"
                                        className="absolute inset-x-1 -bottom-3 h-0.5 bg-[#F5F5F5]"
                                        transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3, ease: EASE }}
                                    />
                                )}
                            </Link>
                        ))}
                    </div>
                    {rightAction && (
                        <>
                            <span className="mx-2 h-3.5 w-px shrink-0 bg-[#F5F5F5]/20" aria-hidden />
                            <span className="shrink-0 text-[#F5F5F5]/70 transition-colors hover:text-[#F5F5F5]">{rightAction}</span>
                        </>
                    )}
                </div>
            </nav>
        </>
    );
}
